import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { HISTORIAL_TAMANO_INICIAL, interpretarRespuestaHistorial } from '../../utils/HelpMediciones';

/**
 * Hook del historial paginado en servidor de un módulo de medición. Es agnóstico
 * del módulo: la función de carga llega por parámetro.
 * @param {{ cargar: Function, numeroDocumento: string, tamanoInicial?: number }} config
 *   cargar(dispatch, { numeroDocumento, page, size }) => Promise de la respuesta
 * @returns {{ estado: 'cargando'|'error'|'listo', filas: Array, totalElements: number,
 *            page: number, size: number, cambiarPagina: Function, cambiarTamano: Function,
 *            reintentar: Function, recargar: Function }}
 */
export default function useHistorialMedicion({
	cargar,
	numeroDocumento,
	tamanoInicial = HISTORIAL_TAMANO_INICIAL,
}) {
	const dispatch = useDispatch();
	// n fuerza una petición nueva aunque page y size no cambien (reintentar, recargar)
	const [consulta, setConsulta] = useState({ page: 0, size: tamanoInicial, n: 0 });
	const [estado, setEstado] = useState('cargando');
	const [filas, setFilas] = useState([]);
	const [totalElements, setTotalElements] = useState(0);
	const ultimaRef = useRef(0);
	const montadoRef = useRef(true);

	useEffect(() => () => {
		montadoRef.current = false;
	}, []);

	useEffect(() => {
		ultimaRef.current += 1;
		const id = ultimaRef.current;
		// Las filas previas se conservan mientras carga (no se pierde el foco del teclado)
		setEstado('cargando');
		Promise.resolve()
			.then(() => cargar(dispatch, { numeroDocumento, page: consulta.page, size: consulta.size }))
			// Un rechazo se trata como fallo del historial
			.then((response) => response, () => undefined)
			.then((response) => {
				// Se descarta lo que llega tarde (petición vieja) o tras desmontar
				if (!montadoRef.current || id !== ultimaRef.current) {
					return;
				}
				const r = interpretarRespuestaHistorial(response);
				if (r.ok) {
					setFilas(r.filas);
					setTotalElements(r.totalElements);
					setEstado('listo');
				} else {
					setFilas([]);
					setEstado('error');
				}
			});
	}, [cargar, dispatch, numeroDocumento, consulta]);

	const cambiarPagina = useCallback((page) => {
		setConsulta((c) => ({ ...c, page, n: c.n + 1 }));
	}, []);
	const cambiarTamano = useCallback((size) => {
		setConsulta((c) => ({ page: 0, size, n: c.n + 1 }));
	}, []);
	const reintentar = useCallback(() => {
		setConsulta((c) => ({ ...c, n: c.n + 1 }));
	}, []);
	const recargar = useCallback(() => {
		setConsulta((c) => ({ ...c, page: 0, n: c.n + 1 }));
	}, []);

	return {
		estado,
		filas,
		totalElements,
		page: consulta.page,
		size: consulta.size,
		cambiarPagina,
		cambiarTamano,
		reintentar,
		recargar,
	};
}
